import React, { useEffect, useState, useRef, useContext } from "react";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import {
  getObjectDimensions,
  getMaxDimension,
  getBoundingBox,
  findMinimumZpoint,
  fitObjectPosition,
} from "../js/three-utils";
import { defaultGrid, minimumDimension } from "../js/three-utils";

import * as THREE from "three";

import { PerspectiveCamera, Edges, CameraControls } from "@react-three/drei";
import GridController from "./GridController";
import { AppContext } from "../contexts/AppContext";

function ViewerWindow() {
  const [object, setObject] = useState(undefined);
  const { file, setFile, setLoader, setNotification } = useContext(AppContext);
  const cameraRef = useRef();
  const edgesRef = useRef();
  const glbMinimumZpoint = 0;
  const glbSimulationMinimumZpoint = -21;

  const onErr = (err) => {
    setNotification({ message: err, style: "alert" });
    setFile(undefined);
    setLoader(false);
  };

  const initializeCamera = (model) => {
    let maxDim = getMaxDimension(getObjectDimensions(getBoundingBox(model)));
    let offset = 3;
    if (maxDim > defaultGrid.size) {
      onErr("The object is too big");
    }
    if (maxDim < minimumDimension) {
      onErr("The object is too small");
    }
    const homeCameraDirection = new THREE.Vector3(1, -1, 1);
    let newCameraPosition = homeCameraDirection.multiplyScalar(offset * maxDim);
    cameraRef.current.position.set(
      newCameraPosition.x,
      newCameraPosition.y,
      newCameraPosition.z
    );
    cameraRef.current.near = maxDim * 0.01;
    cameraRef.current.far = maxDim * 10000;
  };

  useEffect(() => {
    if (
      object !== undefined &&
      object.data !== undefined &&
      cameraRef.current !== undefined
    ) {
      initializeCamera(object.data);
      setLoader(false);
    } // eslint-disable-next-line
  }, [object]);

  const checkForNaNPosition = (obj) => {
    if (obj.type !== "Group") {
      if (obj.geometry.attributes.position.array.includes(NaN)) {
        onErr("Error on file loading");
      }
    } else {
      // eslint-disable-next-line
      obj.children.map((child) => {
        if (child.geometry.attributes.position.array.includes(NaN)) {
          onErr("Error on file loading");
        }
      });
      // eslint-disable-next-line
    }
  };

  useEffect(() => {
    if (file) {
      //if view has been created
      if (file.models[file.currentView] !== undefined) {
        setObject(undefined); //clear object when toggling views
        switch (file.models[file.currentView].format) {
          case "stl":
            new STLLoader().load(
              file.models[file.currentView].url,
              function (geometry) {
                const material = new THREE.MeshPhysicalMaterial({
                  color: "#dde1e4",
                });
                let stlMesh = new THREE.Mesh(geometry, material);
                checkForNaNPosition(stlMesh);
                let minimumZpoint = findMinimumZpoint(
                  stlMesh.geometry.attributes.position.array
                );
                let newObjectPosition = fitObjectPosition(
                  stlMesh,
                  minimumZpoint,
                  {
                    x: 0,
                    y: 0,
                    z: 0,
                  }
                );
                setObject({
                  ...object,
                  position: newObjectPosition,
                  data: stlMesh,
                });
              },
              function () {},
              function (e) {
                onErr("Error on file loading ");
              }
            );
            break;
          case "glb":
            new GLTFLoader().load(
              file.models[file.currentView].url,
              function (glbMesh) {
                var model = glbMesh.scene;
                model.traverse((o) => {
                  if (o.isMesh && o.material !== undefined)
                    o.material.side = THREE.DoubleSide;
                  return o.material;
                });
                glbMesh = glbMesh.scene.children[0];
                let minimumZpoint =
                  file.currentView !== "simulationView"
                    ? glbMinimumZpoint
                    : glbSimulationMinimumZpoint;
                let newObjectPosition = fitObjectPosition(
                  glbMesh,
                  minimumZpoint,
                  {
                    x: 0,
                    y: 0,
                    z: 0,
                  }
                );
                setObject({
                  ...object,
                  position: newObjectPosition, //TODO: PySLM set the object on table. Use separate position per file in order to swithc views
                  data: glbMesh,
                });
              },
              function () {},
              function (e) {
                onErr("Error on file loading ");
              }
            );
            break;
          default:
            break;
        }
      } else {
        setObject(undefined);
      }
    }

    // eslint-disable-next-line
  }, [file]);

  let DOMObject = object !== undefined && object.data !== undefined && (
    <>
      <mesh position={object.position}>
        <primitive object={object.data}>
          <Edges ref={edgesRef} threshold={45} color="#55616d" />
        </primitive>
      </mesh>
    </>
  );

  return (
    <>
      <PerspectiveCamera
        ref={cameraRef}
        makeDefault
        fov={30}
        up={[0, 0, 1]}
        position={[100, 100, 100]}
      >
        <directionalLight position={[-500, 200, 500]} intensity={3} />
        <directionalLight position={[600, -100, 500]} intensity={1} />
        <directionalLight position={[300, 300, -1000]} intensity={3} />
      </PerspectiveCamera>
      <ambientLight intensity={0.5} />
      {DOMObject}
      <CameraControls
        mouseButtons={{
          left: 0,
          right: 1,
          middle: 2,
          wheel: 8,
        }}
      />
      <GridController />
    </>
  );
}

export default ViewerWindow;
